import React, { useMemo } from 'react'
import { useAsync } from 'react-async-hook'
import { Resource } from '@skedulo/protobuf-web/dist/ui/crud'
import {
  handlePlatformError,
  PlatformError,
  throwPlatformError,
} from '../../errors/utils'
import { ApiClientV1 } from '../api'
import { useApiClient } from '../hooks'
import { UserContext, UserMetadata } from './user-context'

const USER_METADATA_PATH = 'custom/usermetadata'

const resolveCurrentUser = async (
  apiClient: ApiClientV1 | null,
  resourceUid: string | undefined,
  userUid: string | undefined,
) => {
  if (!resourceUid || !userUid) return

  try {
    const req = await apiClient?.query.getObject({
      resourceId: resourceUid,
      objectId: userUid,
      relationshipResolution: {
        allow: ['Resources.PrimaryRegion'],
      },
    })
    return req?.result
  } catch (e) {
    const error = e as PlatformError
    throwPlatformError(error.message, error.code, error.traceId)
  }
}

const resolveUserResource = async (
  apiClient: ApiClientV1 | null,
): Promise<Resource | undefined> => {
  try {
    const resource = await apiClient?.ui.resource.getByName('Users')
    return resource === null ? undefined : resource
  } catch (e) {
    const error = e as PlatformError
    throwPlatformError(error.message, error.code, error.traceId)
  }
}

const resolveUserMetadata = async (
  apiClient: ApiClientV1 | null,
): Promise<UserMetadata | undefined> => {
  if (!apiClient) return undefined
  try {
    return await apiClient.services.apiServer.get<UserMetadata>(
      USER_METADATA_PATH,
    )
  } catch (e) {
    handlePlatformError(e)
  }
}

export const UserProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const apiClient = useApiClient()

  const { result: userResource, loading: userResourceLoading } = useAsync(
    resolveUserResource,
    [apiClient],
  )

  const { result: user, loading: userLoading } = useAsync(resolveCurrentUser, [
    apiClient,
    userResource?.uid,
    apiClient?.userId,
  ])

  const { result: metadata, loading: metadataLoading } = useAsync(
    resolveUserMetadata,
    [apiClient],
  )

  const userContextValue = useMemo(
    () => ({
      user,
      metadata,
      loading: userLoading || userResourceLoading,
      metadataLoading,
    }),
    [user, metadata, userLoading, userResourceLoading, metadataLoading],
  )

  return (
    <UserContext.Provider value={userContextValue}>
      {children}
    </UserContext.Provider>
  )
}
